// src/utils/exportUtils.ts
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

/**
 * Export data to JSON format
 * @param data Object to export
 * @param filename Filename without extension
 */
export function exportToJSON(data: any, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], {
    type: 'application/json'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.json`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export data to CSV format
 * @param data Array of objects to export
 * @param filename Filename without extension
 */
export function exportToCSV(data: any[], filename: string): void {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export data to Excel format using xlsx library
 * Generates a proper Excel (.xlsx) file with formatting
 *
 * @param data Array of objects to export
 * @param filename Filename without extension
 */
export function exportToExcel(data: any[], filename: string): void {
  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  // Convert data to worksheet
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Staff Directory');

  // Generate Excel file and trigger download
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

/**
 * Export data to PDF format
 * This is a simplified version that creates a basic HTML table and prints it
 * For a proper PDF export, you would typically use a library like jsPDF
 *
 * @param data Array of objects to export
 * @param filename Filename without extension
 * @param title Optional title for the PDF
 */
export function exportToPDF(data: any[], filename: string, title?: string): void {
  // Create a hidden iframe to print from
  const printFrame = document.createElement('iframe');
  printFrame.style.position = 'absolute';
  printFrame.style.top = '-9999px';
  printFrame.style.left = '-9999px';
  printFrame.style.width = '0';
  printFrame.style.height = '0';
  document.body.appendChild(printFrame);

  // Generate HTML content
  const content = document.createElement('div');

  // Add title if provided
  if (title) {
    const titleEl = document.createElement('h1');
    titleEl.textContent = title;
    titleEl.style.textAlign = 'center';
    content.appendChild(titleEl);
  }

  // Add date
  const dateEl = document.createElement('p');
  dateEl.textContent = `Generated on: ${new Date().toLocaleString()}`;
  dateEl.style.textAlign = 'right';
  content.appendChild(dateEl);

  // Create table
  const table = document.createElement('table');
  table.style.width = '100%';
  table.style.borderCollapse = 'collapse';

  // Add headers
  if (data.length > 0) {
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    Object.keys(data[0]).forEach(key => {
      const th = document.createElement('th');
      th.textContent = key;
      th.style.border = '1px solid #ddd';
      th.style.padding = '8px';
      th.style.backgroundColor = '#f2f2f2';
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);
  }

  // Add data rows
  const tbody = document.createElement('tbody');
  data.forEach(item => {
    const row = document.createElement('tr');
    Object.values(item).forEach(value => {
      const td = document.createElement('td');
      td.textContent = value?.toString() || '';
      td.style.border = '1px solid #ddd';
      td.style.padding = '8px';
      row.appendChild(td);
    });
    tbody.appendChild(row);
  });
  table.appendChild(tbody);
  content.appendChild(table);

  // Write to the iframe and print
  const frameDoc = printFrame.contentWindow?.document;
  if (frameDoc) {
    frameDoc.open();
    frameDoc.write(`
      <html>
        <head>
          <title>${filename}</title>
          <style>
            body { font-family: Arial, sans-serif; }
            @media print {
              body { margin: 0.5cm; }
            }
          </style>
        </head>
        <body>
          ${content.outerHTML}
        </body>
      </html>
    `);
    frameDoc.close();

    // Wait for content to load then print
    setTimeout(() => {
      printFrame.contentWindow?.print();
      // Remove the iframe after printing
      setTimeout(() => {
        document.body.removeChild(printFrame);
      }, 1000);
    }, 500);
  }
}

/**
 * Import data from a CSV file using Papa Parse
 */
export function importCSV(file: File): Promise<any[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: results => {
        if (results.errors && results.errors.length > 0) {
          reject(new Error(`CSV parsing error: ${results.errors[0].message}`));
        } else {
          resolve(results.data as any[]);
        }
      },
      error: error => {
        reject(error);
      }
    });
  });
}